import React from 'react';
import { Link } from 'gatsby';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import loadable from '@loadable/component';

import { BREAKPOINTS, COLORS } from '../../ui/variables';
import { Uppercase } from '../../ui/TypographyStyles';
import { MaxWidthWrapper } from '../../ui/utils';
import ParagraphSmall from '../../ui/ParagraphSmall';

import CareIcon from '../../images/icons/care-icon.svg';
import StarIcon from '../../images/icons/star-icon.svg';
import CardIcon from '../../images/icons/card-icon.svg';
import TelehealthIcon from '../../images/icons/telehealth-icon.svg';
import CalendarIcon from '../../images/icons/calendar-icon.svg';
import DoctorIcon from '../../images/icons/doctor-icon.svg';

const Appointments = loadable(() => import('./Appointments'));

const Container = styled.section`
  background: ${COLORS.NEW_BEIGE};
  padding: 70px 50px;
  @media ${BREAKPOINTS.TABLET} {
    padding-left: 40px;
    padding-right: 40px;
  }
  @media ${BREAKPOINTS.MOBILE} {
    padding: 30px 20px;
  }
`;

const Wrapper = styled(MaxWidthWrapper)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media ${BREAKPOINTS.MOBILE} {
    flex-direction: column;
  }
`;

const Left = styled.div`
  @media (max-width: 960px) {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
`;

const Right = styled.div`
  width: 50%;
  align-self: flex-end;
  @media (max-width: 960px) {
    display: none;
  }
`;

const Region = styled(Uppercase)`
  margin-bottom: 21px;
  color: ${COLORS.ORANGE};
  @media (max-width: 960px) {
    margin-bottom: 24px;
    order: 1;
  }
`;

const LocationName = styled.h1`
  font-family: Adonis;
  font-size: 46px;
  line-height: 92px;
  font-weight: 400;
  letter-spacing: 0.15px;
  margin-bottom: 20px;
  margin-left: -4px;
  text-wrap: balance;
  @media (max-width: 960px) {
    font-size: 42px;
    line-height: 54px;
    margin-bottom: 20px;
    order: 2;
  }
`;

const ValueProps = styled.ul`
  display: flex;
  margin: auto;
  margin-bottom: 40px;
  list-style: none;
  color: ${COLORS.BLACK};
  @media (max-width: 960px) {
    order: 4;
    flex-direction: row;
    margin: 0px 0px 20px;
  }

  li {
    font-size: 16px;
    margin-bottom: 12px;
    padding-left: 32px;
    background-repeat: no-repeat;
    background-position: left center;
    @media (max-width: 1125px) {
      font-size: 14px;
      padding-left: 28px;
    }
    @media (max-width: 960px) {
      font-size: 11px;
      background-size: 20px 16px;
      padding-left: 28px;
    }
  }
`;

const ValuesLeft = styled.div`
  margin-right: 30px;
  @media (max-width: 1125px) {
    margin-right: 20px;
  }
  @media ${BREAKPOINTS.MOBILE} {
    margin-right: 15px;
  }
  a {
    color: ${COLORS.BLACK};
  }
`;

const InfoContainer = styled.div`
  display: inline-block;
  vertical-align: top;
  border-top: 1.5px solid ${COLORS.SECONDARY_GRAY};
  padding-top: 10px;
  @media (max-width: 1120px) {
    max-width: 200px;
  }
  @media (max-width: 960px) {
    max-width: 100%;
    order: 3;
    margin-bottom: 30px;
  }
`;

const InfoContainer2 = styled.div`
  display: inline-block;
  vertical-align: top;
  border-top: 1.5px solid ${COLORS.SECONDARY_GRAY};
  padding-top: 10px;
  margin-left: 40px;
  @media (max-width: 1025px) {
    margin-left: 20px;
  }
  @media (max-width: 960px) {
    margin-left: 0;
    order: 6;
  }
`;

const BusinessDays = styled.div`
  display: inline-block;
  margin-right: 90px;
  @media (max-width: 1050px) {
    margin-right: 70px;
  }
`;

const BusinessHours = styled.div`
  display: inline-block;
  text-align: right;
  @media ${BREAKPOINTS.MOBILE} {
    float: right;
  }
`;

const Text = styled(ParagraphSmall)`
  margin: 0;
  @media (max-width: 1225px) {
    font-size: 12px;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  @media (max-width: 1200px) {
    letter-spacing: 1.5px;
  }
  @media (max-width: 1045px) {
    font-size: 12px;
  }
  @media ${BREAKPOINTS.MOBILE} {
    font-size: 10px;
    letter-spacing: 2.5px;
  }
`;

const Divider = styled.span`
  margin: 0 8px;
  @media (max-width: 1200px) {
    margin: 0 5px;
  }
  @media ${BREAKPOINTS.MOBILE} {
    margin: 0 6px;
  }
`;

const MobileCalendar = styled.div`
  display: none;
  @media (max-width: 960px) {
    display: block;
    order: 5;
    margin-bottom: 35px;
  }
`;

interface Props {
  region: 'new-york-city' | 'boston' | 'washington-dc' | 'chicago';
  locationName: string;
  address1: string;
  address2: string;
  phoneNumber: string;
  email: string;
  days1: string;
  days2: string;
  days3: string;
  hours1: string;
  hours2: string;
  hours3: string;
  locationId: number;
  displayCalendar: boolean;
  isComingSoon: boolean;
  openingDate?: string;
  googleBusinessUrl?: string;
  googleBusinessReviews?: string;
  lat?: number;
  lon?: number;
}

const PracticeMainInfo = ({
  region,
  locationName,
  address1,
  address2,
  phoneNumber,
  email,
  days1,
  days2,
  days3,
  hours1,
  hours2,
  hours3,
  locationId,
  displayCalendar,
  isComingSoon,
  openingDate,
  googleBusinessUrl,
  googleBusinessReviews,
  lat,
  lon,
}: Props) => {
  const removeDash = (str: string) => {
    return str.replace(/-/g, ' ');
  };

  return (
    <Container>
      <Wrapper>
        <Left>
          <Region>
            {/* TODO: remove conditional when chicago region officially opens */}
            {region === 'chicago' ? (
              region
            ) : (
              <Breadcrumb>
                <Link to={'/locations'}>Locations</Link>
                <Divider>/</Divider>
                <Link to={`/locations/${region}`}>{removeDash(region)}</Link>
                <Divider>/</Divider>
                <span>{locationName}</span>
              </Breadcrumb>
            )}
          </Region>
          <LocationName>{locationName} Vet Clinic</LocationName>
          <ValueProps>
            <ValuesLeft>
              <li
                css={css`
                  background: url(${CareIcon});
                `}
              >
                Exceptional veterinary care
              </li>
              <li
                css={css`
                  background: url(${StarIcon});
                `}
              >
                <a href={googleBusinessReviews}>1000+ 5 star reviews</a>
              </li>
              <li
                css={css`
                  background: url(${CardIcon});
                `}
              >
                Insurance handling included
              </li>
            </ValuesLeft>
            <div>
              <li
                css={css`
                  background: url(${TelehealthIcon});
                `}
              >
                24/7 Telehealth
              </li>
              <li
                css={css`
                  background: url(${CalendarIcon});
                `}
              >
                Same or next day appointments
              </li>
              <li
                css={css`
                  background: url(${DoctorIcon});
                `}
              >
                More time with your doctor
              </li>
            </div>
          </ValueProps>
          <InfoContainer>
            <a
              href={
                googleBusinessUrl
                  ? googleBusinessUrl
                  : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
              }
            >
              <Text>
                {address1} {address2}
              </Text>
            </a>
            <a href={`tel:+1 ${phoneNumber}`}>
              <Text>{phoneNumber}</Text>
            </a>
            <a href={`mailto:${email}`}>
              <Text>{email}</Text>
            </a>
          </InfoContainer>
          <InfoContainer2>
            <BusinessDays>
              <Text>{days1}</Text>
              <Text>{days2}</Text>
              <Text>{days3}</Text>
            </BusinessDays>
            <BusinessHours>
              <Text>{hours1}</Text>
              <Text>{hours2}</Text>
              <Text>{hours3}</Text>
            </BusinessHours>
          </InfoContainer2>
          <MobileCalendar>
            <Appointments
              locationId={locationId}
              locationName={locationName}
              showAvailabilities={displayCalendar}
              isComingSoon={isComingSoon}
              openingDate={openingDate}
            />
          </MobileCalendar>
        </Left>
        <Right>
          <Appointments
            locationId={locationId}
            locationName={locationName}
            showAvailabilities={displayCalendar}
            isComingSoon={isComingSoon}
            openingDate={openingDate}
          />
        </Right>
      </Wrapper>
    </Container>
  );
};

export default PracticeMainInfo;
